<script lang="ts">
  import { fade } from "svelte/transition";
  import { createPopperActions } from "svelte-popperjs";
  import autoAnimate from "@formkit/auto-animate";
  import { communitySlug } from "$stores/community";
  import { clickOutside } from "$actions/clickOutside";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import Separator from "$components/+blocks/Separator/Separator.svelte";
  import LoadingAtom from "$components/+blocks/Loading/LoadingAtom.svelte";
  import { DEFAULT_DEBOUNCE_MS } from "$utils/consts";
  import { portal } from "app/actions/portal.svelte";
  import { createEventDispatcher } from "svelte";
  import api from "api";
  import type {
    ForumSpaceWithPermissions,
    ForumSpace,
  } from "@outverse-video/outverse-api-client-lib";
  import { debounce } from "app/utils";
  import { LockSolid, Squircle } from "$components/+blocks/OVIcon";

  export let selectedForumSpace: ForumSpace | undefined;
  const dispatch = createEventDispatcher<{ select: ForumSpace }>();
  // We use the createPopperActions function to create the popperRef and popperContent variables.
  // the popperRef represents the target element.
  // the popperContent represents the element that will be positioned relative to the target element.
  const [popperRef, popperContent] = createPopperActions({
    placement: "bottom-start",
    strategy: "absolute",
  });

  let filter = "";
  let open: boolean;
  let inputElement: HTMLInputElement;
  let filteredForums: ForumSpaceWithPermissions[] = [];

  // reset the filter when the switcher is closed
  $: if (!open) filter = "";
  // focus the input when the switcher is opened
  $: if (open && inputElement) inputElement.focus();
  // extract the filtered forums from the the list of forums with add thread permission

  const filterForums = debounce((search: string) => {
    fetchFilteredForums(search);
  }, DEFAULT_DEBOUNCE_MS);

  async function fetchFilteredForums(search?: string) {
    const response = await api.communitiesForumSpacesList({
      communitySlug: $communitySlug!,
      canAddForumthread: true,
      title: search,
    });
    if (!response.data.results) return;
    filteredForums = response.data.results;
  }
</script>

<button
  use:popperRef
  on:click={() => (open = true)}
  on:keydown={() => (open = true)}
  class="flex items-center gap-x-2 overflow-hidden rounded border-[0.5px] border-black/10 bg-gray-100 px-1.5 py-1 text-sm font-medium transition-colors hover:cursor-pointer hover:bg-gray-200 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10"
  class:glossy-effect={open}
>
  <span style:color={selectedForumSpace?.color}>
    <OVIcon icon={Squircle} class="text-inherit" />
  </span>
  {#if selectedForumSpace?.title}
    <span class="truncate font-medium text-gray-500 dark:text-gray-200">
      {selectedForumSpace?.title}
    </span>
  {/if}
</button>

<!--
  1: use:portal -> Used to attach an element to a DOM node. This is done using the portal action.
  2: use:popperContent -> element that will be positioned relative to the target one.
    Why? aside from all the benefits of using popperjs for automatic positioning, combining this with portal the given solution prevents backdrop-filter stacking issues.
    "By default, the backdrop-filter doesn’t work on an element if its ancestor element has one"
    In the dom, using this solution, the div below will not have a modal as an ancestor, so the backdrop-filter will work.
  -->
{#if open}
  <div
    use:portal={{ target: "body", hasBackLayer: true }}
    use:popperContent={{
      modifiers: [{ name: "offset", options: { offset: [0, 3] } }],
    }}
    use:clickOutside={() => (open = false)}
    transition:fade|global={{ duration: 60 }}
    class="flex max-h-[255px] w-full max-w-[184px] flex-col rounded-md border-[0.5px] border-black/10 glossy-effect py-2.5 dark:border-white/10"
  >
    <input
      bind:this={inputElement}
      bind:value={filter}
      on:input={() => filterForums(filter)}
      placeholder="Change forum..."
      class="mx-4 border-0 bg-transparent text-sm text-gray-500 placeholder-gray-500 focus:outline-none dark:text-gray-400 dark:placeholder-gray-400"
    />

    <Separator class="mx-4 my-2" />

    <div
      use:autoAnimate={{ duration: 180 }}
      class="flex scrollbar-thin flex-col gap-2 overflow-x-hidden overflow-y-auto px-2.5"
    >
      {#await fetchFilteredForums()}
        {#each Array(2) as _}
          <div class="flex items-center space-x-2.5 px-2 py-1">
            <LoadingAtom size="size-5" />
            <LoadingAtom size="h-2 w-20" />
          </div>
        {/each}
      {:then}
        {#each filteredForums as forum (forum.forumSpace.uuid)}
          <button
            on:click|stopPropagation={() => {
              dispatch("select", forum.forumSpace);
              open = false;
            }}
            on:keypress|stopPropagation={() => {
              dispatch("select", forum.forumSpace);
              open = false;
            }}
            tabIndex="0"
            class="flex min-w-0 items-center gap-2 rounded-md p-1.5 text-sm font-medium hover:cursor-pointer hover:bg-black/5 dark:hover:bg-white/10"
          >
            <span style:color={forum.forumSpace.color}>
              <OVIcon icon={Squircle} class="text-inherit" />
            </span>
            <span class="truncate text-gray-500 dark:text-gray-300">{forum.forumSpace.title}</span>
            {#if forum.forumSpace.privacy === "private"}
              <OVIcon
                icon={LockSolid}
                size="w-[11px]"
                class="ml-auto text-gray-500 dark:text-gray-400"
              />
            {/if}
          </button>
        {:else}
          <span class="truncate p-1.5 text-sm dark:text-gray-300">No results</span>
        {/each}
      {/await}
    </div>
  </div>
{/if}
